import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/Table';
import { Skeleton } from '../../../components/ui/Skeleton';
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Bell,
  Clock,
  Eye,
  DollarSign,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  useHealthSummary,
  useFinancialAlerts,
  useAcknowledgeAlert,
} from '../../../hooks/useFinancialSecurity';
import { HealthSummaryCards, CoverageRatioDisplay, CustodyStatusIndicator } from '../../../components/financial-security';
import { AlertNotification } from '../../../components/financial-security/AlertBadge';

export function FinancialHealthDashboard() {
  const { data: healthSummary, isLoading: loadingHealth, refetch: refetchHealth } = useHealthSummary();
  const { data: alerts, isLoading: loadingAlerts } = useFinancialAlerts({ acknowledged: false });
  const acknowledgeAlert = useAcknowledgeAlert();

  const handleAcknowledge = async (alertId: string) => {
    try {
      await acknowledgeAlert.mutateAsync(alertId);
      toast.success('Alerta reconhecido');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao reconhecer alerta');
    }
  };

  const formatCurrency = (value: string) => {
    const num = BigInt(value);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Number(num) / 100);
  };

  if (loadingHealth) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Saúde Financeira</h1>
            <p className="text-muted-foreground">
              Monitoramento de ativos tokenizados
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => refetchHealth()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Summary Cards */}
      {healthSummary && (
        <HealthSummaryCards
          totalAssets={healthSummary.totalAssets}
          healthyAssets={healthSummary.healthyAssets}
          warningAssets={healthSummary.warningAssets}
          criticalAssets={healthSummary.criticalAssets}
        />
      )}

      {/* Yield Metrics */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Distribuições Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span className="text-2xl font-bold">
                  {healthSummary?.pendingYieldDistributions ?? 0}
                </span>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Valor Total</p>
                <p className="font-medium">
                  {formatCurrency(healthSummary?.pendingYieldAmount ?? '0')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={(healthSummary?.failedYieldPayments ?? 0) > 0 ? 'border-red-200 border-2' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pagamentos com Falha
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <XCircle className={`h-5 w-5 ${(healthSummary?.failedYieldPayments ?? 0) > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                <span className={`text-2xl font-bold ${(healthSummary?.failedYieldPayments ?? 0) > 0 ? 'text-red-600' : ''}`}>
                  {healthSummary?.failedYieldPayments ?? 0}
                </span>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Valor Total</p>
                <p className={`font-medium ${(healthSummary?.failedYieldPayments ?? 0) > 0 ? 'text-red-600' : ''}`}>
                  {formatCurrency(healthSummary?.failedYieldAmount ?? '0')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Asset Health Details */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Detalhes dos Ativos</CardTitle>
            <CardDescription>
              Status de saúde individual de cada ativo
            </CardDescription>
          </CardHeader>
          <CardContent>
            {healthSummary?.assetHealthDetails && healthSummary.assetHealthDetails.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ativo</TableHead>
                    <TableHead>Custódia</TableHead>
                    <TableHead>Cobertura</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {healthSummary.assetHealthDetails.map((asset) => (
                    <TableRow key={asset.assetId}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{asset.assetName}</p>
                          <p className="text-xs text-muted-foreground">{asset.symbol}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <CustodyStatusIndicator status={asset.custodyStatus} size="sm" />
                      </TableCell>
                      <TableCell>
                        <CoverageRatioDisplay
                          ratio={asset.coverageRatio}
                          showThreshold={false}
                          size="sm"
                        />
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            asset.healthStatus === 'HEALTHY'
                              ? 'default'
                              : asset.healthStatus === 'WARNING'
                              ? 'secondary'
                              : 'destructive'
                          }
                          className="gap-1"
                        >
                          {asset.healthStatus === 'HEALTHY' && <CheckCircle2 className="h-3 w-3" />}
                          {asset.healthStatus === 'WARNING' && <AlertTriangle className="h-3 w-3" />}
                          {asset.healthStatus === 'CRITICAL' && <XCircle className="h-3 w-3" />}
                          {asset.healthStatus === 'HEALTHY' ? 'Saudável' : asset.healthStatus === 'WARNING' ? 'Atenção' : 'Crítico'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Link to={`/admin/tokenization/assets/${asset.assetId}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nenhum ativo tokenizado</p>
                <Link to="/admin/tokenization/assets/new">
                  <Button variant="link">Criar primeiro ativo</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Alerts */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              <CardTitle className="text-lg">Alertas</CardTitle>
              {alerts && alerts.length > 0 && (
                <Badge variant="destructive">{alerts.length}</Badge>
              )}
            </div>
            <CardDescription>
              Alertas de segurança ativos
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAlerts ? (
              <Skeleton className="h-32" />
            ) : alerts && alerts.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {alerts.slice(0, 5).map((alert) => (
                  <AlertNotification
                    key={alert.id}
                    title={alert.title}
                    message={alert.message}
                    severity={alert.severity}
                    time={formatDistanceToNow(new Date(alert.createdAt), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                    onAcknowledge={() => handleAcknowledge(alert.id)}
                  />
                ))}
                {alerts.length > 5 && (
                  <Link to="/admin/tokenization/security-audit" className="w-full">
                    <Button variant="link" className="w-full">
                      Ver todos os {alerts.length} alertas
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500 opacity-50" />
                <p>Nenhum alerta ativo</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Last Updated */}
      {healthSummary?.lastUpdated && (
        <p className="text-xs text-muted-foreground text-center">
          Última atualização: {formatDistanceToNow(new Date(healthSummary.lastUpdated), {
            addSuffix: true,
            locale: ptBR,
          })}
        </p>
      )}
    </div>
  );
}

export default FinancialHealthDashboard;
